/**
 * Normalize a raw `chat.response.input.required` payload into the view-model
 * the in-chat HITL gate card renders.
 *
 * The payload lands in the `hitlRequest` slot as `payload.data ?? payload`
 * (see `useChatStreaming.handleInputRequired`), so its keys are the engine's
 * `InputRequiredData`:
 *
 *   { interaction_id, status, expires_at,
 *     ui_block: { block_id, block_type, placement, data: <render blob> },
 *     resume_match: { gate?, ... } }
 *
 * Inner keys are snake_case on the wire but we accept camelCase too (`??`),
 * per the platform boundary convention.
 *
 * Returns `null` when the payload is not a chat-renderable gate — the card
 * then simply does not show, and the durable My Tasks inbox still covers the
 * decision. Two gate kinds render in chat:
 *
 * - `hitl_approval` → `mode: 'approval'` (Approve / Reject + comments)
 * - `hitl_qa`       → `mode: 'qa'` (conversational `{gate, done, message}`,
 *   e.g. a discovery interview turn: answer or continue)
 * - `hitl_choice`   → `mode: 'choice'` (option-chip questions, `{gate, done,
 *   message, answers, research_requested, research_questions}`, e.g. the
 *   discovery brief gate: pick options, type a custom answer, or ask the
 *   workspace to research a question)
 * - `hitl_pick`     → `mode: 'pick'` (choose a vault document, `{gate,
 *   document_uuid}`, e.g. the discovery document picker at the start)
 *
 * Anything else (e.g. remaining `hitl_form` custom-JSON contracts) stays
 * inbox-only.
 *
 * @param {object|null|undefined} raw
 * @returns {{ interactionId: string, blockType: string,
 *   mode: 'approval'|'qa'|'choice'|'pick', render: object,
 *   gate: string|undefined, status: string|undefined,
 *   expiresAt: string|undefined, createdAt: string|undefined } | null}
 */
export const mapInputRequiredToGateCard = (raw) => {
  if (!raw || typeof raw !== 'object') return null;

  const interactionId = raw.interaction_id ?? raw.interactionId;
  if (!interactionId) return null;

  const uiBlock = raw.ui_block ?? raw.uiBlock;
  if (!uiBlock || typeof uiBlock !== 'object') return null;

  const blockType = uiBlock.block_type ?? uiBlock.blockType;
  const MODE_BY_BLOCK = {
    hitl_approval: 'approval',
    hitl_qa: 'qa',
    hitl_choice: 'choice',
    hitl_pick: 'pick',
  };
  const mode = MODE_BY_BLOCK[blockType] ?? null;
  if (!mode) return null;

  const resumeMatch = raw.resume_match ?? raw.resumeMatch ?? {};

  return {
    interactionId,
    blockType,
    mode,
    render: uiBlock.data ?? {},
    gate: resumeMatch.gate,
    status: raw.status,
    expiresAt: raw.expires_at ?? raw.expiresAt,
    createdAt: raw.created_at ?? raw.createdAt,
  };
};

export default mapInputRequiredToGateCard;
